// ─────────────────────────────────────────────────────────────────────────────
// documentsApi — client-side helpers for the drawings / documents feature.
//
// Talks to backend endpoints spec'd in outputs/SFM-drawings-backend-spec.md:
//
//   GET /api/document?job_id=&pmk=&fabricated_item_id=&latest_only=&approval_status=
//   GET /api/document/:id
//   GET /api/job/:id/drawings
//   GET /api/task/:id/drawing
//
// All calls are DEFENSIVE: if the endpoint 404s (because the backend hasn't
// shipped the feature yet) we return a graceful empty result instead of
// throwing. Callers render an empty-state or fall back to legacy fields
// (task.image, job.drawing_pdf_url).
//
// Contract for consumers:
//   - Every function returns `{ documents, error, notAvailable }` where:
//       documents   — array of document objects (possibly empty)
//       error       — Error instance for non-404 failures
//       notAvailable — true when the endpoint returned 404 (backend not wired)
//   - Never throws. Callers can safely `const { documents } = await listByJob(id)`
//     without a try/catch.
//
// Display-only. No mutations live in this file — uploads still go through
// DocumentUpload.jsx POST to /document/upload.
// ─────────────────────────────────────────────────────────────────────────────

import ApiCall from "./apicall";

const isNotFound = (err) =>
  err?.response?.status === 404 ||
  err?.response?.data?.message?.toLowerCase?.().includes("not found") ||
  err?.response?.data?.message?.toLowerCase?.().includes("cannot get");

const empty = (notAvailable = false, error = null) => ({
  documents: [],
  document: null,
  drift: null,
  error,
  notAvailable,
});

/**
 * List documents by job id (optionally latest-rev-per-PMK only).
 *
 * Pass `null` (or omit) for jobId to fetch ALL documents the user has access
 * to (tenant-scoped on the backend). This is the "Documents Library / All jobs"
 * code path used by `src/pages/Documents/DocumentsLibrary.jsx`.
 *
 * BUG FIX 2026-05-02 (CR-2026-FE-DOCUMENTS-LIBRARY-FETCH): previously this
 * function returned an empty array immediately when jobId was null, which
 * caused the Documents Library page to render "0 documents" because no API
 * call ever fired. Now we make the call without a job_id param so the backend
 * returns all tenant-scoped documents.
 *
 * @param {number|string|null} jobId  null = all jobs (tenant-scoped)
 * @param {object} [opts]
 * @param {boolean} [opts.latestOnly=true]
 * @param {'approved'|'pending'|'superseded'|'rejected'|'archived'|'all'} [opts.approvalStatus='approved']
 *   'archived' is a meta-status (CR-2026-DOCUMENTS-ARCHIVE-RESTORE 2026-05-05)
 *   that returns ONLY soft-deleted rows (deletedAt IS NOT NULL). Backend
 *   recognizes either ?approval_status=archived OR ?status=archived.
 */
export async function listByJob(jobId, opts = {}) {
  const { latestOnly = true, approvalStatus = "approved" } = opts;
  try {
    const params = new URLSearchParams({
      latest_only: latestOnly ? "true" : "false",
    });
    if (jobId || jobId === 0) {
      params.set("job_id", String(jobId));
    }
    if (approvalStatus && approvalStatus !== "all") {
      params.set("approval_status", approvalStatus);
    }
    const res = await ApiCall.get(`/document?${params.toString()}`);
    const arr =
      res?.data?.data?.documents ||
      res?.data?.documents ||
      res?.data?.data ||
      (Array.isArray(res?.data) ? res.data : []) ||
      [];
    return { documents: arr, error: null, notAvailable: false };
  } catch (err) {
    if (isNotFound(err)) return empty(true);
    return empty(false, err);
  }
}

/**
 * Convenience wrapper using /api/job/:id/drawings.
 * Falls back to listByJob if that endpoint isn't live either.
 */
export async function listDrawingsForJob(jobId) {
  if (!jobId && jobId !== 0) return empty();
  try {
    const res = await ApiCall.get(`/job/${encodeURIComponent(jobId)}/drawings`);
    const arr =
      res?.data?.data?.drawings ||
      res?.data?.data ||
      res?.data?.drawings ||
      (Array.isArray(res?.data) ? res.data : []) ||
      [];
    return { documents: arr, error: null, notAvailable: false };
  } catch (err) {
    if (isNotFound(err)) {
      // Fall back to the generic list endpoint
      return listByJob(jobId, { latestOnly: true });
    }
    return empty(false, err);
  }
}

/**
 * Resolve the drawing that a task should display, honouring Rev lock.
 * Returns { document, drift } — document is null when no match exists.
 */
export async function getDrawingForTask(taskId) {
  if (!taskId && taskId !== 0) return { document: null, drift: null, error: null, notAvailable: false };
  try {
    const res = await ApiCall.get(`/task/${encodeURIComponent(taskId)}/drawing`);
    return {
      document: res?.data?.data?.document || null,
      drift: res?.data?.data?.drift || null,
      error: null,
      notAvailable: false,
    };
  } catch (err) {
    if (isNotFound(err)) {
      return { document: null, drift: null, error: null, notAvailable: true };
    }
    return { document: null, drift: null, error: err, notAvailable: false };
  }
}

/**
 * List documents matching a PMK within a job.
 */
export async function listByPmk(jobId, pmk) {
  if (!pmk) return empty();
  try {
    const params = new URLSearchParams({ pmk: String(pmk) });
    if (jobId || jobId === 0) params.set("job_id", String(jobId));
    const res = await ApiCall.get(`/document?${params.toString()}`);
    const arr =
      res?.data?.data?.documents ||
      res?.data?.documents ||
      (Array.isArray(res?.data) ? res.data : []) ||
      [];
    return { documents: arr, error: null, notAvailable: false };
  } catch (err) {
    if (isNotFound(err)) return empty(true);
    return empty(false, err);
  }
}

/**
 * List documents linked to a fabricated item.
 */
export async function listByFabricatedItem(fabricatedItemId) {
  if (!fabricatedItemId && fabricatedItemId !== 0) return empty();
  try {
    const params = new URLSearchParams({
      fabricated_item_id: String(fabricatedItemId),
    });
    const res = await ApiCall.get(`/document?${params.toString()}`);
    const arr =
      res?.data?.data?.documents ||
      res?.data?.documents ||
      (Array.isArray(res?.data) ? res.data : []) ||
      [];
    return { documents: arr, error: null, notAvailable: false };
  } catch (err) {
    if (isNotFound(err)) return empty(true);
    return empty(false, err);
  }
}

/**
 * Fetch a single document by id.
 */
export async function getDocument(id) {
  if (!id && id !== 0) return { document: null, error: null, notAvailable: false };
  try {
    const res = await ApiCall.get(`/document/${encodeURIComponent(id)}`);
    return {
      document: res?.data?.data || res?.data?.document || res?.data || null,
      error: null,
      notAvailable: false,
    };
  } catch (err) {
    if (isNotFound(err)) return { document: null, error: null, notAvailable: true };
    return { document: null, error: err, notAvailable: false };
  }
}

/**
 * Soft-delete a document.
 *
 * Backend route: DELETE /api/document/:id
 * Lives on the live droplet (per CLAUDE.md 2026-04-28 deleteDocument deploy).
 * The backend enforces:
 *   - Approved drawings refuse to delete (returns 403, records preservation
 *     per AISC 303 §4 / ISO 9001 §7.5.3)
 *   - Soft-delete only (sets deletedAt + deletedBy; row not removed)
 *   - 30-day recovery window (matches AISC bulk-delete pattern)
 *   - audit_log row written (V-03 evidence-capture)
 *
 * Frontend gates the button on status !== 'approved' as defense-in-depth,
 * but the server is authoritative.
 *
 * @param {number|string} id — document id
 * @param {string} [reason] — audit-trail reason (passed in body); optional but
 *                            recommended for audit defense
 * @returns {Promise<{ok: boolean, error: Error|null, status: number|null}>}
 */
export async function deleteDocument(id, reason) {
  if (!id && id !== 0) return { ok: false, error: new Error("id required"), status: null };
  try {
    const body = reason ? { reason } : {};
    const res = await ApiCall.delete(`/document/${encodeURIComponent(id)}`, { data: body });
    return { ok: true, error: null, status: res?.status || 200 };
  } catch (err) {
    return {
      ok: false,
      error: err,
      status: err?.response?.status || null,
    };
  }
}

/**
 * Restore a soft-deleted (archived) document.
 *
 * Backend route: PATCH /api/document/:id/restore (CR-2026-DOCUMENTS-ARCHIVE-RESTORE 2026-05-05).
 * The backend enforces:
 *   - Tenant scoping (401 if no tenant context)
 *   - Document must currently be archived; restoring a non-archived doc returns 409
 *   - 404 if document not found at all
 *   - On success: clears deletedAt via Sequelize `paranoid: true` model.restore()
 *
 * @param {number|string} id — document id (must be in the archived set)
 * @param {string} [reason] — audit-trail reason (optional, future audit_log wiring)
 * @returns {Promise<{ok: boolean, error: Error|null, status: number|null}>}
 */
export async function restoreDocument(id, reason) {
  if (!id && id !== 0) return { ok: false, error: new Error("id required"), status: null };
  try {
    const body = reason ? { reason } : {};
    const res = await ApiCall.patch(`/document/${encodeURIComponent(id)}/restore`, body);
    return { ok: true, error: null, status: res?.status || 200 };
  } catch (err) {
    return {
      ok: false,
      error: err,
      status: err?.response?.status || null,
    };
  }
}

export default {
  listByJob,
  listDrawingsForJob,
  getDrawingForTask,
  listByPmk,
  listByFabricatedItem,
  getDocument,
  deleteDocument,
  restoreDocument,
};
